import React, { useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as lib from '~/dependencies';
import * as actionTypes from '~/store/actions/actionTypes';
import {
  aggregateIntegrationDetails,
  composeExternalUrl,
  IntegrationIds,
  integrationContactState,
} from '~/helpers/contactsIntegration';
import {
  getNumberByCallDirection,
  getFullNameFormatted,
  getIntegrationInfo,
  isExtension,
  isNotAnonymous,
} from '~/helpers';
import ExpansibleContacts from '../ExpansibleContacts';

import { Row } from './styles';

const { CUSTOM_URLS_ID, ODOO_ID, GO_HIGH_LEVEL_ID } = IntegrationIds;

export default function DetailsInsights({ details }) {
  const dispatch = useDispatch();

  const { user_name } = useSelector(state => state.auth);
  const connectedIntegrations = useSelector(
    state => state.integrations.accountIntegrations
  );
  const allCrmContacts = useSelector(state => state.integrations.contacts);

  const phoneNumber = getNumberByCallDirection(details);

  useEffect(() => {
    return () => {
      if (details.callId) {
        dispatch({
          type: actionTypes.CLEAR_CONTACTS_INTEGRATIONS,
          payload: {
            callId: details.callId,
          },
        });
      }
    };
  }, [details, dispatch]);

  useEffect(() => {
    // If does not found local contacts store in redux then pick from call logs integration object
    if (
      details.callId &&
      !lib._.find(allCrmContacts, con => con.callId === details.callId)
    ) {
      // Normalize the object to be igual to the ones received by getIntegratedContacts()

      const [crmContacts, customUrls] = lib._.partition(
        details.integrations,
        i => i.integration !== CUSTOM_URLS_ID
      );

      const crmContactsNormalized = lib._.map(crmContacts, obj => {
        return {
          callId: details.callId,
          integrationId: Number(obj.integration),
          ...obj.contact,
          /**
           * Some integrations (Odoo, GoHighLevel) need to have a composite integrationId
           * to identify the contact integration account.
           * This happens because call logs API does not return the integration in the same format as v1/integrations API.
           * @see GoHighLevel multiple accounts on NWA-2438
           */
          ...(obj.integration === ODOO_ID && {
            integrationId: Number(
              `${obj.integration}.${obj.contact.integration_account_id}`
            ),
          }),
          ...(obj.integration === GO_HIGH_LEVEL_ID && {
            integrationId: Number(
              `${obj.integration}.${obj.contact.integration_account_id}`
            ),
          }),
        };
      });

      const customUrlsNormalized = lib._.map(customUrls, obj => {
        return {
          callId: details.callId,
          integrationId: Number(obj.integration),
          ...(obj.urls && { name: obj.urls.name }),
          ...(obj.urls && { url: obj.urls.url }),
          ...(obj.urls && { firstName: phoneNumber }),
          integrationInfo: aggregateIntegrationDetails(
            Number(obj.integration),
            connectedIntegrations
          ),
        };
      });

      // The call logs API returns contacts also for disabled integration, we have to check if contact belongs to an active integration.
      const activeCrmContacts = lib._.filter(crmContactsNormalized, contact => {
        return lib._.some(
          connectedIntegrations,
          integration =>
            Number(integration.id) === Number(contact.integrationId)
        );
      });

      // JS Conversion: Snake-Case to CamelCase
      const convertedContacts = lib._.map(activeCrmContacts, c => ({
        callId: c.callId,
        contactState: integrationContactState.CREATED,
        integrationId: c.integrationId,
        firstName: c.firstname,
        lastName: c.lastname,
        companyName: c.company_name,
        ownerName: c.owner_name,
        externalId: c.external_id,
        externalPortalId: c.external_portal_id,
        fullNameFormatted: getFullNameFormatted(c),
        integrationAccountId: c.integration_account_id,
        externalUrl: composeExternalUrl({
          connectedIntegrations,
          integrationId: c.integrationId,
          externalId: c.external_id,
        }),
        integrationInfo: aggregateIntegrationDetails(
          c.integrationId,
          connectedIntegrations
        ),
      }));

      // Include pre-contact to be saved in case user has integrations but NOT contacts
      lib._.forEach(
        connectedIntegrations.filter(i => Number(i.id) !== CUSTOM_URLS_ID),
        acc => {
          if (
            !lib._.find(
              convertedContacts,
              con => Number(con.integrationId) === Number(acc.id)
            )
          ) {
            convertedContacts.push({
              callId: details.callId,
              contactState: integrationContactState.DRAFT,
              integrationId: acc.id,
              firstName: phoneNumber,
              phoneNumber,
              companyName: null,
              ownerName: user_name,
              integrationInfo: getIntegrationInfo(
                acc.id,
                connectedIntegrations
              ),
            });
          }
        }
      );

      dispatch({
        type: actionTypes.STORE_CONTACTS_INTEGRATIONS,
        payload: {
          contacts: lib._.cloneDeep([
            ...convertedContacts,
            ...customUrlsNormalized,
          ]),
        },
      });
    }
  }, [
    connectedIntegrations,
    details,
    dispatch,
    allCrmContacts,
    phoneNumber,
    user_name,
  ]);

  // Group contacts per integrations
  const contactsGroupedByintegrationId = lib._.chain(allCrmContacts)
    .filter(c => {
      return c.callId === details.callId;
    })
    .groupBy('integrationId')
    .value();

  function logCrmContacts() {
    // eslint-disable-next-line no-console
    console.table(allCrmContacts);
  }

  return (
    <>
      {phoneNumber &&
        !isExtension(phoneNumber) &&
        isNotAnonymous(phoneNumber) &&
        lib._.map(contactsGroupedByintegrationId, (contacts, integrationId) => {
          return (
            <Fragment key={integrationId}>
              <Row
                id={`integration-${integrationId}`}
                onDoubleClick={logCrmContacts}>
                <ExpansibleContacts
                  contacts={contacts}
                  border="0"
                  bordercolor="unset"
                  borderradius="unset"
                  backgroundcolor="#f9f9f9"
                  expanded={false}
                />
              </Row>
            </Fragment>
          );
        })}
    </>
  );
}

DetailsInsights.defaultProps = {
  details: null,
};

DetailsInsights.propTypes = {
  details: lib.PropTypes.instanceOf(Object),
};
